import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TerrainRelief, type Heightmap } from './TerrainRelief';
import { TerrainTiles } from './TerrainTiles';
import {
  fetchTerrainMap,
  terrainImageUrl,
  type CivilianZone,
  type Definition,
  type Force,
  type IntelNote,
  type MapMarker,
  type Objective,
  type PlacedComposition,
  type RestrictedZone,
  type Shape,
  type TerrainMapView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import {
  shapeCenter,
  type Layer,
  type LayerState,
  type MapPlace,
  type Selection,
  type Tool,
} from './designModel';
import {
  centredView,
  clampView,
  FIT_VIEW,
  fitBounds,
  panBy,
  sameView,
  screenToMap,
  STEP,
  zoomAt,
  type MapView,
  type WorldBounds,
} from './mapView';
import { NatoSymbol } from './NatoSymbol';
import { colourOf } from './natoSymbols';
import {
  arrangeMarks,
  brushClass,
  LABEL_SPAN_PX,
  SECTOR_HATCH,
  areaLabel,
  iconLabel,
  labelAt,
  labelScale,
  polygonBounds,
  polygonCentroid,
  SECTOR_NOTE_SPAN_PX,
  SECTOR_SPAN_PX,
  sectorWords,
  areaFrame,
  shapeBounds,
  spanOnScreen,
} from './mapMarks';

/**
 * The terrain image from the panel with the whole mission drawn on it.
 *
 * It began as objective pins on a square and is now the editing surface the
 * designer works on: the area of operations and the zones inside it, the
 * markers the players will see, the enemy the players will not, the civilian
 * zones, the intelligence pins and the objectives, each on a layer that can be
 * turned off. A tool decides what a click does — place a marker, drop a patrol,
 * draw a box — and anything already placed can be dragged where it belongs.
 *
 * It still has no dependency: one CSS transform on the wrapper everything lives
 * in, a counter-scale on each pin so a symbol stays readable however close the
 * map is, and an SVG overlay in the same coordinate space for the shapes and
 * the routes. The screen-to-world conversion is `mapView.screenToMap`, which is
 * unit-tested, so what is placed lands where the pointer is at any zoom.
 *
 * Read-only callers (the session and direction pages) pass objectives and
 * nothing else and get exactly what they had.
 */

export interface MapLayersInput {
  objectives?: Objective[];
  /**
   * How each objective ended, by id (`done`, `failed`, anything else is left plain): the pin
   * takes a colour for it. Only the ended session's page passes it.
   */
  results?: Record<string, string>;
  start?: number[];
  area?: Definition['area'];
  markers?: MapMarker[];
  forces?: Force[];
  compositions?: PlacedComposition[];
  /**
   * Where people died, as the panel counted it. Drawn under everything else:
   * it is the ground the night was fought over, and the objectives and the
   * marks are what is read on top of it.
   */
  heat?: { x: number; y: number; weight: number }[];
  /**
   * Where the players went, as the panel counted it (its report's «Zones més
   * trepitjades»): drawn under the deaths, in the players' own colour.
   */
  movement?: { x: number; y: number; weight: number }[];
  intel?: IntelNote[];
  civilians?: CivilianZone[];
  /**
   * The places the generator answered (release D, D11.8): the base, the way
   * out and the script's places. Selected, never dragged.
   */
  places?: MapPlace[];
}

interface Props extends MapLayersInput {
  world: string;
  worldSize: number;
  /** Which layers to draw; everything when absent. */
  layers?: LayerState;
  selected?: Selection | string | null;
  onSelect?: (selection: Selection) => void;
  /** A click on empty ground, with the tool the caller holds. */
  onPlace?: (x: number, y: number) => void;
  /**
   * A plain press on the map itself (not a pin, not the end of a drag), for a small map that
   * opens its large view. The cursor says so.
   */
  onOpen?: () => void;
  /** An element dragged to a new place. */
  onMove?: (selection: Selection, x: number, y: number) => void;
  /** A rectangle drawn corner to corner, in metres. */
  onDrawBox?: (cx: number, cy: number, a: number, b: number) => void;
  tool?: Tool;
  height?: number;
  /**
   * The map takes the whole width of its place and is less tall than wide when the window
   * asks for it (about 72% of its height), instead of a square with an empty band on each
   * side. It then shows a band of the terrain, opens on the area of operations when there is
   * one, and pans to the rest. For the pages whose map is the page: the session, its
   * briefing, the direction and the designer. Without it nothing changes.
   */
  wide?: boolean;
  /** Metres per grid square; 0 turns the grid off. */
  grid?: number;
  /**
   * The terrain survey's height grid, drawn as relief under everything else.
   * A dedicated server can never take a picture of a terrain, so this is the
   * only map the platform can make of one that has no Steam preview.
   */
  relief?: Heightmap | null;
  /**
   * Where the map opens: the whole terrain, or framed on the area of
   * operations with a margin round it (`area` is needed for that; without
   * one the terrain is what there is). A person who has zoomed or panned is
   * not re-framed; the fit button goes back and forth between the two.
   */
  fit?: 'terrain' | 'area';
}

export type MissionMapProps = Props;

/** The icon of each kind of place the generator answered. */
const PLACE_ICON: Record<MapPlace['kind'], string> = {
  base: 'bi-house-fill',
  insertion: 'bi-box-arrow-in-right',
  extraction: 'bi-box-arrow-right',
  place: 'bi-geo-alt-fill',
};

/** The old callers pass a string; the designer passes a selection. */
function selectionOf(selected: Props['selected']): Selection | null {
  if (!selected) return null;
  return typeof selected === 'string' ? { kind: 'objective', id: selected } : selected;
}

const same = (a: Selection | null, b: Selection) => !!a && a.kind === b.kind && a.id === b.id;

/** The tools that draw a box by dragging instead of placing by clicking. */
const BOX_TOOLS: Tool[] = ['area', 'zone'];

export function MissionMap({
  world,
  worldSize: definedSize,
  objectives = [],
  results,
  start,
  area,
  markers = [],
  forces = [],
  compositions = [],
  heat = [],
  movement = [],
  intel = [],
  civilians = [],
  places = [],
  layers,
  selected,
  onSelect,
  onPlace,
  onOpen,
  onMove,
  onDrawBox,
  tool = 'select',
  height = 420,
  wide = false,
  grid = 0,
  relief = null,
  fit = 'terrain',
}: Props) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState(height);
  /** The box's own height: the square's side, or less on a wide map. */
  const [tall, setTall] = useState(height);
  const [view, setView] = useState<MapView>(FIT_VIEW);
  /** Set once a person has zoomed or panned: from then on the map is theirs and nothing re-frames it. */
  const touched = useRef(false);
  /** The hatch patterns' id prefix: one map's patterns are its own. */
  const hatchId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  /** Pointers down on the map: one pans, two pinch. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<number | null>(null);
  /** A drag is not a click: set while the pointer moves, read by the click handler. */
  const dragged = useRef(false);
  /** What is being dragged, when something is. */
  const moving = useRef<Selection | null>(null);
  /** The pointer the map has captured, when a drag has actually started. */
  const captured = useRef<number | null>(null);
  /** The box being drawn, in world metres, so it survives a pan or a zoom mid-drag. */
  const [drawing, setDrawing] = useState<{ from: [number, number]; to: [number, number] } | null>(
    null,
  );
  /**
   * The Atlas's tile pyramid for this terrain, when it has one. Asked once per
   * world; a terrain it does not carry answers `available: false` and the flat
   * picture below is what gets drawn, exactly as before.
   */
  const [atlas, setAtlas] = useState<TerrainMapView | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAtlas(null);
    fetchTerrainMap(world)
      .then((map) => {
        if (!cancelled) setAtlas(map.available ? map : null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [world]);

  /**
   * The terrain's edge the map draws to. The Atlas measures the terrain it has
   * tiles of, and where it does, its measure is the map's square: a
   * definition's `worldSize` can be a default nobody measured (Kunduz River's
   * 20 480 m for a 10 240 m terrain, memory `a-default-is-not-a-measurement`),
   * and the tiles then filled a quarter of the square, the rest dark, whatever
   * the zoom (the owner, test card 160, 2026-10-17: the whole map must fill the
   * square). Both start at the south-west corner, so a position is the same
   * point on either.
   */
  const worldSize = atlas && atlas.sizeInMeters > 0 ? atlas.sizeInMeters : definedSize;

  const on = (layer: Layer) => !layers || layers[layer];
  const current = selectionOf(selected);

  // Measured, clamped and framed before the browser paints (layout effects, in this order):
  // a wide map settles in two passes, and the first must not be seen nor clicked on.
  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    // A container that has not been laid out yet reports 0, and a square of 0 turns
    // every screen-to-world conversion into a NaN; the asked-for height stands in.
    // The square is as wide as the frame it sits in and never taller than the
    // window, so a map asked for at 900 on a laptop is not cut by the fold: the
    // frame is measured rather than the map, which is only as wide as its square
    // and sits centred in a wider column instead of leaving a dark band beside it.
    const measure = () => {
      if (wide) {
        // As wide as its place; never taller than wide, nor than most of the window.
        const width = Math.max(1, el.clientWidth || height);
        const room =
          window.innerHeight > 0 ? Math.max(280, Math.round(window.innerHeight * 0.72)) : width;
        setSize(width);
        setTall(Math.max(1, Math.min(width, room)));
        return;
      }
      const room = window.innerHeight > 0 ? Math.max(280, window.innerHeight - 96) : height;
      const side = Math.max(1, Math.min(el.clientWidth || height, height, room));
      setSize(side);
      setTall(side);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [height, wide]);

  // A narrower box means a smaller square: the pan has to come back inside it.
  useLayoutEffect(() => setView((v) => clampView(v, size, tall)), [size, tall]);
  /** Where the map opens with nothing to frame: the whole terrain, or the middle band of it. */
  const home = useMemo<MapView>(
    () => (tall < size ? centredView(size, tall) : FIT_VIEW),
    [size, tall],
  );

  /**
   * The view that frames the area of operations, when the caller asks for it
   * and there is one. It depends on the square's size, so it is computed
   * again when the frame is measured, and applied as long as nobody has
   * taken the map in hand.
   */
  // The objectives' places as one key: a page that re-renders every second hands a new array each
  // time, and the frame is the same until a place changes.
  const placesKey = objectives
    .map((o) => `${o.type}:${(o.position ?? []).join(',')}:${o.radius ?? 0}`)
    .join('|');
  const frameBounds = useMemo(
    () => (fit === 'area' && area ? areaFrame(area, objectives) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fit, area, placesKey],
  );
  const framed = useMemo<MapView | null>(
    () => (frameBounds ? fitBounds(frameBounds, size, worldSize, 0.1, tall) : null),
    [frameBounds, size, tall, worldSize],
  );
  useLayoutEffect(() => {
    if (!touched.current) setView(framed ?? home);
  }, [framed, home]);

  // The wheel listener cannot be a React prop: it has to be non-passive to stop the page scrolling.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      touched.current = true;
      const rect = el.getBoundingClientRect();
      setView((v) =>
        zoomAt(
          v,
          Math.exp(-e.deltaY * 0.002),
          e.clientX - rect.left,
          e.clientY - rect.top,
          size,
          tall,
        ),
      );
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  }, [size, tall]);

  /** One size for every pin on the map, whatever the layer: the symbols used to be 18, 22 and 26. */
  const PIN = 20;
  /** World metres to canvas pixels; the canvas is the square the transform scales. */
  const px = (x: number) => (x / worldSize) * size;
  const py = (y: number) => size - (y / worldSize) * size;
  /** Undoes the wrapper's zoom for one marker, so pins stay readable at any scale. */
  const pin = (x: number, y: number) => ({
    left: px(x),
    top: py(y),
    // Counter-scaled only when zoomed in: a pin keeps its size close up and
    // shrinks with the map when the whole terrain is on screen (the owner,
    // 2026-09-10: the symbols filled the map at the terrain zoom).
    transform: `translate(-50%, -50%) scale(${1 / Math.max(1, view.scale)})`,
  });
  const metres = (m: number) => (m / worldSize) * size;
  /** At the whole-terrain zoom every label lands on every other one; close in they help. */
  const labelled = (selection: Selection) => view.scale >= 2.5 || same(current, selection);
  /** Undoes the wrapper's zoom for an SVG label, as `pin` does for a pin: text is a screen size, not a map size. */
  const k = labelScale(view.scale);
  /** How many screen pixels a shape takes, by its box; 0 for a shape with no box. */
  const spanOf = (b: WorldBounds | null) =>
    b ? spanOnScreen(Math.max(b.maxX - b.minX, b.maxY - b.minY), view.scale, size, worldSize) : 0;
  /** Canvas pixels to screen pixels (the box's), and back: the labels are arranged on the screen. */
  const toScreen = (cx: number, cy: number): [number, number] => [
    cx * view.scale + view.x,
    cy * view.scale + view.y,
  ];
  const fromScreen = (sx: number, sy: number): [number, number] => [
    (sx - view.x) / view.scale,
    (sy - view.y) / view.scale,
  ];

  function toMap(clientX: number, clientY: number, rect: DOMRect): [number, number] {
    return screenToMap(clientX - rect.left, clientY - rect.top, view, size, worldSize);
  }

  function pointerDown(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    dragged.current = false;
    // Deliberately NOT capturing the pointer here. A captured pointer retargets
    // the pointerup, and with it the click, to the capturing element: every
    // click on a pin was delivered to the map instead of to the pin, so nothing
    // on the map could ever be selected with a real mouse (a synthetic
    // element.click() still worked, which is why the tests did not see it).
    // The capture is taken in pointerMove, once a drag has actually started —
    // by then the gesture is a drag and no click is owed to anybody.
    if (BOX_TOOLS.includes(tool) && onDrawBox) {
      // A box about to be drawn is an area about to change: the view stays where the person has it.
      touched.current = true;
      const at = toMap(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());
      setDrawing({ from: at, to: at });
    }
  }

  function pointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const from = pointers.current.get(event.pointerId);
    if (!from) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()];
    if (points.length >= 2) {
      const rect = event.currentTarget.getBoundingClientRect();
      const [a, b] = points;
      const spread = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2 - rect.left;
      const midY = (a.y + b.y) / 2 - rect.top;
      const was = pinch.current;
      pinch.current = spread;
      dragged.current = true;
      touched.current = true;
      if (was && spread > 0) setView((v) => zoomAt(v, spread / was, midX, midY, size, tall));
      return;
    }
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    if (dx === 0 && dy === 0) return;
    if (Math.abs(dx) + Math.abs(dy) > 2) {
      dragged.current = true;
      // From here the gesture is a drag: capture the pointer so it keeps
      // arriving when it leaves the pin, the map or the window.
      if (captured.current !== event.pointerId) {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        captured.current = event.pointerId;
      }
    }

    if (drawing) {
      const at = toMap(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect());
      setDrawing((d) => (d ? { ...d, to: at } : d));
      return;
    }
    // Dragging an element moves it; dragging the map pans it.
    const held = moving.current;
    if (held && onMove) {
      const rect = event.currentTarget.getBoundingClientRect();
      const [x, y] = toMap(event.clientX, event.clientY, rect);
      onMove(held, x, y);
      return;
    }
    touched.current = true;
    setView((v) => panBy(v, dx, dy, size, tall));
  }

  function pointerUp(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (captured.current === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      captured.current = null;
    }
    if (pointers.current.size < 2) pinch.current = null;
    if (drawing && onDrawBox) {
      const rect = event.currentTarget.getBoundingClientRect();
      const [x1, y1] = drawing.from;
      const [x2, y2] = toMap(event.clientX, event.clientY, rect);
      const a = Math.abs(x2 - x1) / 2;
      const b = Math.abs(y2 - y1) / 2;
      setDrawing(null);
      // A tap with a box tool is not a box: it would make one of no size.
      if (a > 20 && b > 20) {
        onDrawBox((x1 + x2) / 2, (y1 + y2) / 2, a, b);
        dragged.current = true;
        return;
      }
    }
    moving.current = null;
  }

  function click(event: React.MouseEvent<HTMLDivElement>) {
    if (dragged.current) {
      dragged.current = false;
      return;
    }
    if (!onPlace) {
      onOpen?.();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const [x, y] = toMap(event.clientX, event.clientY, rect);
    onPlace(x, y);
  }

  const zoomBy = (factor: number) => {
    touched.current = true;
    setView((v) => zoomAt(v, factor, size / 2, tall / 2, size, tall));
  };
  /** The fit button: to the area of operations when there is one and the map is not on it, else the whole terrain. */
  const fitsArea = !!framed && !sameView(view, framed);
  const fitView = () => {
    touched.current = true;
    setView(fitsArea && framed ? framed : home);
  };

  /** A pin's shared props: selection, dragging and the class that says what it is. */
  const pinProps = (selection: Selection, label: string) => ({
    className: `pin ${selection.kind} ${same(current, selection) ? 'selected' : ''}`,
    style: pin(0, 0),
    title: label,
    'aria-label': label,
    'aria-pressed': same(current, selection),
    // Deliberately not stopping the event: the map's own pointerdown has to run
    // too, because it is what records the pointer that pointermove reads. Without
    // it the map ignored every move and nothing could be dragged.
    onPointerDown: () => {
      if (onMove) moving.current = selection;
    },
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      if (dragged.current) {
        dragged.current = false;
        return;
      }
      onSelect?.(selection);
    },
  });

  /**
   * One shape as an SVG element in canvas pixels. `color` is set as well as
   * `stroke` so a fill of `currentColor` in the stylesheet is the shape's own
   * colour and not the page's text colour.
   */
  function shapeSvg(shape: Shape, key: string, className: string, colour: string) {
    if (shape.shape === 'polygon') {
      const points = (shape.points ?? []).map((p) => `${px(p[0])},${py(p[1])}`).join(' ');
      return (
        <polygon
          key={key}
          className={className}
          points={points}
          stroke={colour}
          style={{ color: colour }}
        />
      );
    }
    const [cx, cy] = shapeCenter(shape);
    const a = metres(shape.a ?? 0);
    const b = metres(shape.b ?? 0);
    const transform = `rotate(${-(shape.angle ?? 0)} ${px(cx)} ${py(cy)})`;
    if (shape.shape === 'ellipse') {
      return (
        <ellipse
          key={key}
          className={className}
          cx={px(cx)}
          cy={py(cy)}
          rx={a}
          ry={b}
          stroke={colour}
          style={{ color: colour }}
          transform={transform}
        />
      );
    }
    return (
      <rect
        key={key}
        className={className}
        x={px(cx) - a}
        y={py(cy) - b}
        width={a * 2}
        height={b * 2}
        stroke={colour}
        style={{ color: colour }}
        transform={transform}
      />
    );
  }

  /**
   * Where a shape's name stands, in world metres: on its edge, at the bearing
   * its meaning has (the platform's MapMarks), since the game's map shows no
   * text for a shape. A polygon's is on its northmost corner.
   */
  function labelPoint(shape: Shape, edge: number): [number, number] | null {
    if (shape.shape === 'polygon') {
      const top = (shape.points ?? []).reduce(
        (best, p) => (p[1] > best[1] ? p : best),
        [0, -Infinity],
      );
      return Number.isFinite(top[1]) ? [top[0], top[1]] : null;
    }
    return labelAt(
      shape.shape,
      shapeCenter(shape),
      shape.a ?? 0,
      shape.b ?? 0,
      shape.angle ?? 0,
      edge,
    );
  }

  /**
   * Every text written on the map's shapes, gathered before any is drawn so
   * none is written over another: a sector's letter inside its sector (an
   * anchor: it never moves), the area's name on its corner, then the markers'
   * and the zones' names on their edges. Each is drawn at a fixed size on the
   * screen, so a shape too small to be told apart at this zoom (fewer than
   * LABEL_SPAN_PX pixels across) carries no name until the map comes closer,
   * unless it is the one selected; the rest are arranged the way the runtime
   * arranges the game's marks (`arrangeMarks`): a line north or south when
   * two cross, merged into the one it stood on when no line is free.
   */
  type MapLabel = {
    id: string;
    at: [number, number];
    text: string;
    colour: string;
    rank: number;
    sector?: { letter: string; note: string; wide: boolean };
  };
  const wanted: MapLabel[] = [];
  const shown = (b: WorldBounds | null, least: number, selection: Selection) =>
    spanOf(b) >= least || same(current, selection);
  if (on('markers')) {
    for (const m of markers) {
      if (m.kind !== 'polygon' || !m.text) continue;
      const bounds = polygonBounds(m.points);
      const selection: Selection = { kind: 'marker', id: m.id };
      if (!bounds || !shown(bounds, SECTOR_SPAN_PX, selection)) continue;
      const centre =
        m.position && m.position.length >= 2
          ? ([m.position[0], m.position[1]] as [number, number])
          : polygonCentroid(m.points);
      if (!centre) continue;
      const words = sectorWords(m.text);
      wanted.push({
        id: m.id,
        at: centre,
        text: m.text,
        colour: colourOf(m.colour),
        // An anchor: the letter stands on its sector, never a line off it (card 159).
        rank: 0,
        sector: { ...words, wide: spanOf(bounds) >= SECTOR_NOTE_SPAN_PX },
      });
    }
  }
  if (on('area') && area?.label) {
    const at = labelPoint(area, area.labelEdge ?? 315);
    if (at && shown(shapeBounds(area), LABEL_SPAN_PX, { kind: 'area', id: 'area' })) {
      wanted.push({
        id: 'area',
        at,
        text: area.label,
        colour: colourOf(area.colour ?? 'ColorBlack'),
        rank: 2,
      });
    }
  }
  if (on('markers')) {
    for (const m of markers) {
      if ((m.kind !== 'rect' && m.kind !== 'ellipse') || !m.text) continue;
      const shape: Shape = {
        shape: m.kind,
        center: m.position,
        a: m.a ?? 200,
        b: m.b ?? 200,
        angle: m.angle,
      };
      const at = labelPoint(shape, m.labelEdge ?? 0);
      if (at && shown(shapeBounds(shape), LABEL_SPAN_PX, { kind: 'marker', id: m.id })) {
        // What the area is, never the bare name of a place the map already names (mapMarks.areaLabel).
        const text = areaLabel(m, { objectives, markers, civilians, zones: area?.restricted });
        wanted.push({ id: m.id, at, text, colour: colourOf(m.colour), rank: 3 });
      }
    }
  }
  if (on('zones')) {
    (area?.restricted ?? []).forEach((z: RestrictedZone, i) => {
      if (!z.label) return;
      const id = z.id ?? `zone_${i}`;
      const at = labelPoint(z, z.labelEdge ?? 90);
      if (at && shown(shapeBounds(z), LABEL_SPAN_PX, { kind: 'zone', id: z.id ?? '' })) {
        wanted.push({ id, at, text: z.label, colour: colourOf(z.colour ?? 'ColorGUER'), rank: 3 });
      }
    });
  }
  /**
   * The pins' texts are arranged with the shapes' (the owner, 2026-10-11:
   * «Casa del sector A», «Reducte», «Informe previ» one over the other at the
   * village). A pin's icon is an anchor nobody may cover — its own label
   * included, so a label that has to move goes under, not over its icon. The
   * ranks are the runtime's: the base's, the extraction's and an objective's
   * own mark stay (0); a shape's name moves before a marker's text (2) does;
   * what only the editors see (forces, notes, civilians, the generator's
   * places) gives way to all of it (3).
   */
  type PinText = { key: string; at: number[]; text: string; rank: number };
  const pinTexts: PinText[] = [];
  const pinAnchors: PinText[] = [];
  const pinText = (
    key: string,
    at: number[],
    text: string | undefined,
    rank: number,
    sel: Selection,
  ) => {
    if (!at || at.length < 2) return;
    pinAnchors.push({ key: `${key}:icon`, at, text: '', rank: 0 });
    if (text && labelled(sel)) pinTexts.push({ key, at, text, rank });
  };
  if (on('markers')) {
    for (const m of markers) {
      if (m.kind !== 'icon') continue;
      const anchor = m.id === 'start' || m.id === 'extraction' || m.id.startsWith('obj_');
      // What must be done at a secondary place, never the place's bare name (mapMarks.iconLabel).
      const text = iconLabel(m, { objectives, markers, civilians, zones: area?.restricted });
      pinText(`marker:${m.id}`, m.position, text, anchor ? 0 : 3, { kind: 'marker', id: m.id });
    }
  }
  if (on('compositions')) {
    for (const c of compositions)
      pinText(`composition:${c.id}`, c.position, c.label, 4, { kind: 'composition', id: c.id });
  }
  if (on('forces')) {
    for (const f of forces)
      pinText(`force:${f.id}`, f.position, f.label, 4, { kind: 'force', id: f.id });
  }
  if (on('intel')) {
    for (const n of intel) {
      if (n.position) pinText(`intel:${n.id}`, n.position, n.title, 4, { kind: 'intel', id: n.id });
    }
  }
  if (on('civilians')) {
    civilians.forEach((z, i) =>
      pinText(`civilian:${z.id ?? `civ_${i}`}`, z.position, z.label, 4, {
        kind: 'civilian',
        id: z.id ?? `civ_${i}`,
      }),
    );
  }
  if (on('places')) {
    for (const p of places)
      pinText(`place:${p.id}`, p.position, p.label, 4, { kind: 'place', id: p.id });
  }

  /** A text line on the screen: the label font is 11 px, a character about 6.4 px wide. */
  const LINE_PX = 14;
  const CHAR_PX = 6.4;
  /** A pin's label is centred under its icon: its text starts half its width to the west, a line and a bit below. */
  const PIN_LABEL_DY = 10.5;
  /** The icon itself stands on the point (measured in the browser, 2026-10-12: a 20 px box centred on it). */
  const PIN_ICON_DY = 0;
  const arranged = arrangeMarks(
    [
      ...wanted.map((l) => {
        const [sx, sy] = toScreen(px(l.at[0]), py(l.at[1]));
        return {
          id: l.id,
          x: sx,
          y: sy,
          len: l.sector
            ? 2 + (l.sector.wide && l.sector.note ? l.sector.note.length + 1 : 0)
            : l.text.length,
          rank: l.rank,
        };
      }),
      ...pinAnchors.map((p) => {
        const [sx, sy] = toScreen(px(p.at[0]), py(p.at[1]));
        return { id: p.key, x: sx, y: sy + PIN_ICON_DY, len: 0, rank: 0 };
      }),
      ...pinTexts.map((p) => {
        const [sx, sy] = toScreen(px(p.at[0]), py(p.at[1]));
        return {
          id: p.key,
          x: sx - (p.text.length * CHAR_PX) / 2,
          y: sy + PIN_LABEL_DY,
          len: p.text.length,
          rank: p.rank,
          // One line at most: a pin's label pushed three lines off it read as a pin in the wrong
          // place (card 159). One that cannot stay within a line waits for the map to come closer.
          max: 1,
        };
      }),
    ],
    LINE_PX,
    { char: CHAR_PX, up: -1 },
  );
  const byId = new Map<string, { text: string }>([
    ...wanted.map((l) => [l.id, l] as const),
    ...pinTexts.map((p) => [p.key, p] as const),
  ]);
  /**
   * A merged shape's label joins the one it stood on, as in game. A pin's does not: its text
   * written at another pin named that pin wrongly; it is left out until the map comes closer.
   */
  const pinKeys = new Set(pinTexts.map((p) => p.key));
  const joined = new Map<string, string[]>();
  for (const a of arranged) {
    const text = byId.get(a.id)?.text;
    if (a.how === 'merged' && a.into && text && !pinKeys.has(a.id))
      joined.set(a.into, [...(joined.get(a.into) ?? []), text]);
  }
  /** Where a pin's label ends up: moved by whole lines, or hidden because merged. */
  const pinPlaced = new Map<string, { dy: number; hidden: boolean }>();
  for (const a of arranged) {
    const p = pinTexts.find((t) => t.key === a.id);
    if (!p) continue;
    const [, sy] = toScreen(px(p.at[0]), py(p.at[1]));
    pinPlaced.set(a.id, { dy: a.y - (sy + PIN_LABEL_DY), hidden: a.how === 'merged' });
  }
  const pinLabel = (key: string, text: string) => {
    const placed = pinPlaced.get(key);
    if (placed?.hidden) return null;
    const words = [...new Set([text, ...(joined.get(key) ?? [])])].join(' · ');
    return (
      <span
        className="pin-label"
        style={placed && placed.dy !== 0 ? { transform: `translateY(${placed.dy}px)` } : undefined}
      >
        {words}
      </span>
    );
  };
  const labelsSvg = arranged
    .filter((a) => a.how !== 'merged' && byId.has(a.id) && wanted.some((l) => l.id === a.id))
    .map((a) => {
      const label = wanted.find((l) => l.id === a.id)!;
      const [cx, cy] = fromScreen(a.x, a.y);
      const extra = joined.get(a.id) ?? [];
      const transform = `translate(${cx} ${cy}) scale(${k})`;
      if (label.sector) {
        const { letter, note, wide } = label.sector;
        const strong = note !== '';
        return (
          <g
            key={`${a.id}_label`}
            className={`sector-label ${strong ? 'strongpoint' : ''}`}
            style={{ color: label.colour }}
            transform={transform}
          >
            <title>{[...new Set([label.text, ...extra])].join(' · ')}</title>
            {strong && <circle className="ring" r={12.5} />}
            <circle className="disc" r={9} />
            <text className="letter">{letter}</text>
            {wide && (note || extra.length > 0) && (
              <text className="note" x={strong ? 16 : 13}>
                {[note, ...extra].filter(Boolean).join(' · ')}
              </text>
            )}
          </g>
        );
      }
      return (
        <g
          key={`${a.id}_label`}
          className="shape-label"
          style={{ color: label.colour }}
          transform={transform}
        >
          <circle r={3} />
          <text x={6}>{[...new Set([label.text, ...extra])].join(' · ')}</text>
        </g>
      );
    });

  /**
   * The hatch of a sector, one pattern a colour: stripes 8 px apart on the screen at any zoom, as the
   * game's, light enough to read the houses under it (SECTOR_HATCH).
   */
  const hatchColours = useMemo(
    () => [...new Set(markers.filter((m) => m.kind === 'polygon').map((m) => colourOf(m.colour)))],
    [markers],
  );
  const hatchOf = (hex: string) => `${hatchId}-hatch-${hex.replace('#', '')}`;
  const hatchPitch = 8 * k;

  const gridLines: number[] = [];
  if (grid > 0) {
    for (let m = grid; m < worldSize; m += grid) gridLines.push(m);
  }

  const cursor =
    tool !== 'select'
      ? 'crosshair'
      : onPlace
        ? 'crosshair'
        : onOpen
          ? 'zoom-in'
          : view.scale > 1
            ? 'grab'
            : 'default';

  return (
    <div className="map-frame" ref={frame}>
      <div className="map-pane" style={{ width: size }}>
        <div
          ref={box}
          className={`map tool-${tool}`}
          style={{ width: '100%', height: tall, touchAction: 'none', cursor }}
          onClick={click}
          onPointerDown={pointerDown}
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
          role="img"
          aria-label={world}
        >
          <div
            className="map-canvas"
            style={{
              width: size,
              height: size,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
              transformOrigin: '0 0',
            }}
          >
            {atlas && (
              <TerrainTiles
                world={world}
                map={atlas}
                size={size}
                worldSize={worldSize}
                view={view}
              />
            )}
            {relief && !atlas && <TerrainRelief map={relief} size={size} />}
            {!failed && !relief && !atlas && (
              <img
                src={terrainImageUrl(world, 'map')}
                alt=""
                width={size}
                height={size}
                onError={() => setFailed(true)}
                draggable={false}
              />
            )}

            <svg
              className="map-shapes"
              width={size}
              height={size}
              viewBox={`0 0 ${size} ${size}`}
              aria-hidden="true"
              // The strokes' counter-scale: `vector-effect: non-scaling-stroke` does
              // not see the CSS transform on the canvas above (Firefox drew every
              // outline 2.5 px × zoom), so the stylesheet multiplies by this instead.
              style={{ '--k': k } as React.CSSProperties}
            >
              {hatchColours.length > 0 && (
                <defs>
                  {hatchColours.map((hex) => (
                    <pattern
                      key={hex}
                      id={hatchOf(hex)}
                      width={hatchPitch}
                      height={hatchPitch}
                      patternUnits="userSpaceOnUse"
                      patternTransform="rotate(45)"
                    >
                      <rect
                        width={hatchPitch}
                        height={hatchPitch}
                        fill={hex}
                        opacity={SECTOR_HATCH.ground}
                      />
                      <rect
                        width={hatchPitch}
                        height={hatchPitch / 2}
                        fill={hex}
                        opacity={SECTOR_HATCH.stripe}
                      />
                    </pattern>
                  ))}
                </defs>
              )}
              {gridLines.map((m) => (
                <g key={m} className="grid">
                  <line x1={px(m)} y1={0} x2={px(m)} y2={size} />
                  <line x1={0} y1={py(m)} x2={size} y2={py(m)} />
                </g>
              ))}

              {on('area') &&
                area &&
                shapeSvg(
                  area,
                  'area',
                  `shape area ${same(current, { kind: 'area', id: 'area' }) ? 'selected' : ''}`,
                  colourOf(area.colour ?? 'ColorBlack'),
                )}

              {on('zones') &&
                (area?.restricted ?? []).map((z: RestrictedZone, i) =>
                  shapeSvg(
                    z,
                    z.id ?? `zone_${i}`,
                    `shape zone ${z.kind ?? 'nogo'} ${brushClass(z.brush ?? 'DiagGrid')} ${same(current, { kind: 'zone', id: z.id ?? '' }) ? 'selected' : ''}`,
                    colourOf(z.colour ?? 'ColorGUER'),
                  ),
                )}

              {on('markers') &&
                markers
                  .filter((m) => m.kind !== 'icon')
                  .map((m) =>
                    m.kind === 'polyline' ? (
                      <polyline
                        key={m.id}
                        className={`shape line ${m.audience === 'staff' ? 'staff' : ''} ${same(current, { kind: 'marker', id: m.id }) ? 'selected' : ''}`}
                        points={(m.points ?? []).map((p) => `${px(p[0])},${py(p[1])}`).join(' ')}
                        stroke={colourOf(m.colour)}
                      />
                    ) : m.kind === 'polygon' ? (
                      // A village's sector, as the game hatches it: its own outline,
                      // stripes in the holder's colour (the enemy's red where the cell
                      // is, the players' blue where nobody is) and a border; its letter
                      // is drawn with the other labels, over everything.
                      polygonBounds(m.points) ? (
                        <polygon
                          key={m.id}
                          className={`shape mark sector ${same(current, { kind: 'marker', id: m.id }) ? 'selected' : ''}`}
                          points={(m.points ?? []).map((p) => `${px(p[0])},${py(p[1])}`).join(' ')}
                          stroke={colourOf(m.colour)}
                          style={{
                            color: colourOf(m.colour),
                            fill: `url(#${hatchOf(colourOf(m.colour))})`,
                          }}
                        />
                      ) : null
                    ) : (
                      shapeSvg(
                        {
                          shape: m.kind === 'ellipse' ? 'ellipse' : 'rect',
                          center: m.position,
                          a: m.a ?? 200,
                          b: m.b ?? 200,
                          angle: m.angle,
                        },
                        m.id,
                        `shape mark ${brushClass(m.brush)} ${m.audience === 'staff' ? 'staff' : ''} ${same(current, { kind: 'marker', id: m.id }) ? 'selected' : ''}`,
                        colourOf(m.colour),
                      )
                    ),
                  )}

              {on('civilians') &&
                civilians.map((z, i) => (
                  <circle
                    key={z.id ?? `civ_${i}`}
                    className={`shape civilians ${same(current, { kind: 'civilian', id: z.id ?? '' }) ? 'selected' : ''}`}
                    cx={px(z.position[0])}
                    cy={py(z.position[1])}
                    r={metres(z.radius)}
                  />
                ))}

              {on('objectives') &&
                objectives
                  .filter((o) => (o.radius ?? 0) > 0 && Array.isArray(o.position))
                  .map((o) => (
                    <circle
                      key={`r_${o.id}`}
                      className={`shape objective ${o.priority ?? 'main'}`}
                      cx={px(o.position[0])}
                      cy={py(o.position[1])}
                      r={metres(o.radius ?? 0)}
                    />
                  ))}

              {[
                { points: movement, className: 'heat movement', key: 'm' },
                { points: heat, className: 'heat', key: 'h' },
              ].map(
                ({ points, className, key }) =>
                  points.length > 0 &&
                  (() => {
                    // Weight is a count of what happened in that square, and one
                    // square with fifty deaths must not make forty others invisible:
                    // the square root of the share is what keeps the quiet ones on
                    // the map at all.
                    const heaviest = Math.max(...points.map((h) => h.weight));
                    return (
                      <g key={key}>
                        {points.map((h, i) => (
                          <circle
                            key={i}
                            className={className}
                            cx={px(h.x)}
                            cy={py(h.y)}
                            r={metres(30 + 45 * Math.sqrt(h.weight / heaviest))}
                            opacity={0.12 + 0.38 * Math.sqrt(h.weight / heaviest)}
                          />
                        ))}
                      </g>
                    );
                  })(),
              )}

              {on('compositions') &&
                compositions.map((c) => {
                  // Its own reach: the objects when they are resolved into the
                  // mission, the cleared ground when they are not yet. A
                  // composition an editor has just dropped has neither, and a
                  // fixed 25 m is still an honest "about this big".
                  const reach =
                    c.objects && c.objects.length
                      ? Math.max(
                          ...c.objects.map((o) => Math.hypot(o.offset[0] ?? 0, o.offset[1] ?? 0)),
                        )
                      : (c.clearRadius ?? 25);
                  return (
                    <g key={`c_${c.id}`} className={`composition-shape side-${c.side ?? 'west'}`}>
                      <circle
                        className={`radius ${same(current, { kind: 'composition', id: c.id }) ? 'selected' : ''}`}
                        cx={px(c.position[0])}
                        cy={py(c.position[1])}
                        r={metres(Math.max(10, reach))}
                      />
                      {(c.objects ?? []).map((o, i) => (
                        <rect
                          key={i}
                          className="composition-object"
                          x={px(c.position[0] + (o.offset[0] ?? 0)) - metres(1.5)}
                          y={py(c.position[1] + (o.offset[1] ?? 0)) - metres(1.5)}
                          width={metres(3)}
                          height={metres(3)}
                        />
                      ))}
                    </g>
                  );
                })}

              {on('forces') &&
                forces.map((f) => (
                  <g key={`f_${f.id}`} className="force-shape">
                    {(f.waypoints ?? []).length > 1 && (
                      <polyline
                        className={`route ${same(current, { kind: 'force', id: f.id }) ? 'selected' : ''}`}
                        points={[
                          ...(f.waypoints ?? []),
                          ...(f.cycle !== false ? [(f.waypoints ?? [])[0]] : []),
                        ]
                          .filter(Boolean)
                          .map((p) => `${px(p[0])},${py(p[1])}`)
                          .join(' ')}
                      />
                    )}
                    {(f.waypoints ?? []).length === 0 && (f.radius ?? 0) > 0 && (
                      <circle
                        className={`radius ${same(current, { kind: 'force', id: f.id }) ? 'selected' : ''}`}
                        cx={px(f.position[0])}
                        cy={py(f.position[1])}
                        r={metres(f.radius ?? 0)}
                      />
                    )}
                    {f.watch && f.watch.length >= 2 && (
                      <line
                        className="watch"
                        x1={px(f.position[0])}
                        y1={py(f.position[1])}
                        x2={px(f.watch[0])}
                        y2={py(f.watch[1])}
                      />
                    )}
                  </g>
                ))}

              {/* The names, over every shape and arranged so none hides another. */}
              {labelsSvg}

              {drawing && (
                <rect
                  className="shape drawing"
                  x={Math.min(px(drawing.from[0]), px(drawing.to[0]))}
                  y={Math.min(py(drawing.from[1]), py(drawing.to[1]))}
                  width={Math.abs(px(drawing.to[0]) - px(drawing.from[0]))}
                  height={Math.abs(py(drawing.to[1]) - py(drawing.from[1]))}
                />
              )}
            </svg>

            {start && start.length >= 2 && (
              <span
                className="marker start"
                style={pin(start[0], start[1])}
                title={ca.mapUi.startPoint}
              >
                &#9650;
              </span>
            )}

            {on('objectives') &&
              objectives.map((o, i) =>
                // A hidden objective's place is not in a player's copy: no pin for it.
                !Array.isArray(o.position) ? null : (
                  <button
                    type="button"
                    key={o.id}
                    className={`marker ${o.priority ?? 'main'} ${results?.[o.id] ? `result-${results[o.id]}` : ''} ${same(current, { kind: 'objective', id: o.id }) ? 'selected' : ''}`}
                    style={pin(o.position[0], o.position[1])}
                    title={o.priority === 'hidden' ? ca.hiddenObjective : (o.title ?? o.id)}
                    // The pin's text is its number, so without this its name would be "3".
                    aria-label={o.priority === 'hidden' ? ca.hiddenObjective : (o.title ?? o.id)}
                    aria-pressed={same(current, { kind: 'objective', id: o.id })}
                    onPointerDown={() => {
                      if (onMove) moving.current = { kind: 'objective', id: o.id };
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (dragged.current) {
                        dragged.current = false;
                        return;
                      }
                      onSelect?.({ kind: 'objective', id: o.id });
                    }}
                  >
                    {o.priority === 'hidden' ? '?' : i + 1}
                  </button>
                ),
              )}

            {on('markers') &&
              markers
                .filter((m) => m.kind === 'icon')
                .map((m) => {
                  const selection: Selection = { kind: 'marker', id: m.id };
                  const text = iconLabel(m, {
                    objectives,
                    markers,
                    civilians,
                    zones: area?.restricted,
                  });
                  const props = pinProps(selection, text || m.symbol || m.id);
                  return (
                    <button
                      type="button"
                      key={m.id}
                      {...props}
                      className={`${props.className} ${m.audience === 'staff' ? 'staff' : ''}`}
                      style={pin(m.position[0], m.position[1])}
                    >
                      <NatoSymbol
                        symbol={m.symbol ?? 'mil_dot'}
                        colour={m.colour}
                        size={PIN}
                        // A place's dot has no name of its own to show on hover but the place's.
                        title={m.symbol?.startsWith('place_') ? text : undefined}
                      />
                      {text && labelled(selection) && pinLabel(`marker:${m.id}`, text)}
                    </button>
                  );
                })}

            {on('compositions') &&
              compositions.map((c) => {
                const selection: Selection = { kind: 'composition', id: c.id };
                const props = pinProps(selection, c.label || c.id);
                return (
                  <button
                    type="button"
                    key={c.id}
                    {...props}
                    className={`${props.className} composition side-${c.side ?? 'west'}`}
                    style={pin(c.position[0], c.position[1])}
                  >
                    <i className="bi bi-bounding-box" aria-hidden="true" />
                    {c.label && labelled(selection) && pinLabel(`composition:${c.id}`, c.label)}
                  </button>
                );
              })}

            {on('forces') &&
              forces.map((f) => {
                const selection: Selection = { kind: 'force', id: f.id };
                const props = pinProps(selection, f.label || f.id);
                return (
                  <button
                    type="button"
                    key={f.id}
                    {...props}
                    className={`${props.className} role-${f.role}`}
                    style={pin(f.position[0], f.position[1])}
                  >
                    <NatoSymbol symbol={forceSymbol(f.role, f.side)} size={PIN} />
                    {f.label && labelled(selection) && pinLabel(`force:${f.id}`, f.label)}
                  </button>
                );
              })}

            {on('intel') &&
              intel
                .filter((n) => n.position && n.position.length >= 2)
                .map((n) => {
                  const selection: Selection = { kind: 'intel', id: n.id };
                  const props = pinProps(selection, n.title || n.id);
                  return (
                    <button
                      type="button"
                      key={n.id}
                      {...props}
                      className={`${props.className} ${n.confidence ?? 'probable'}`}
                      style={pin(n.position![0], n.position![1])}
                    >
                      <i className="bi bi-info-circle-fill" aria-hidden="true" />
                      {n.title && labelled(selection) && pinLabel(`intel:${n.id}`, n.title)}
                    </button>
                  );
                })}

            {on('civilians') &&
              civilians.map((z, i) => {
                const selection: Selection = { kind: 'civilian', id: z.id ?? `civ_${i}` };
                const props = pinProps(selection, z.label || ca.mapUi.civilians);
                return (
                  <button
                    type="button"
                    key={z.id ?? `civ_${i}`}
                    {...props}
                    style={pin(z.position[0], z.position[1])}
                  >
                    <i className="bi bi-people-fill" aria-hidden="true" />
                    {z.label &&
                      labelled(selection) &&
                      pinLabel(`civilian:${z.id ?? `civ_${i}`}`, z.label)}
                  </button>
                );
              })}

            {on('places') &&
              places.map((p) => {
                const selection: Selection = { kind: 'place', id: p.id };
                const props = pinProps(selection, p.label);
                return (
                  <button
                    type="button"
                    key={p.id}
                    {...props}
                    // A place moves only among its kind's candidates, never by a drag.
                    onPointerDown={undefined}
                    className={`${props.className} place-${p.kind}`}
                    style={pin(p.position[0], p.position[1])}
                  >
                    <i className={`bi ${PLACE_ICON[p.kind]}`} aria-hidden="true" />
                    {labelled(selection) && pinLabel(`place:${p.id}`, p.label)}
                  </button>
                );
              })}
          </div>
        </div>
        <div
          className="map-zoom"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="small"
            aria-label={ca.mapUi.zoomIn}
            title={ca.mapUi.zoomIn}
            onClick={() => zoomBy(STEP)}
          >
            <i className="bi bi-zoom-in" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="small"
            aria-label={ca.mapUi.zoomOut}
            title={ca.mapUi.zoomOut}
            onClick={() => zoomBy(1 / STEP)}
          >
            <i className="bi bi-zoom-out" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="small"
            aria-label={fitsArea ? ca.mapUi.fitArea : ca.mapUi.fit}
            title={fitsArea ? ca.mapUi.fitArea : ca.mapUi.fit}
            onClick={fitView}
          >
            <i
              className={`bi ${fitsArea ? 'bi-bounding-box-circles' : 'bi-arrows-fullscreen'}`}
              aria-hidden="true"
            />
          </button>
          <span className="chip num" aria-hidden="true">
            {ca.mapUi.zoomLevel(view.scale)}
          </span>
        </div>
        {/* The Atlas publishes the credit for every map it carries, and a map
          drawn from somebody else's work says whose it is. */}
        {atlas && (
          <p className="map-credit">
            {atlas.attribution ? `${atlas.attribution} · ` : ''}
            {ca.mapUi.atlasCredit}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The game's own NATO symbol for a force, by what it is and whose it is.
 *
 * These used to be Bootstrap glyphs — a house, an eye, a truck — which is a
 * second visual language on a map that already speaks Arma's: the markers
 * beside them are the game's own pictures. Every name below is a marker Arma
 * ships (`\a3\ui_f\data\map\markers\nato\`), so `NatoSymbol` serves the
 * real one and falls back to its drawing only when the panel is not answering.
 */
const FORCE_ROLE: Record<Force['role'], string> = {
  patrol: 'inf',
  garrison: 'installation',
  guard: 'recon',
  static: 'support',
  vehicle: 'motor_inf',
  civilian: 'unknown',
};

/** b/o/n/c, the four sides the game's marker set is drawn for. */
const SIDE_LETTER: Record<string, string> = {
  west: 'b',
  blufor: 'b',
  east: 'o',
  opfor: 'o',
  resistance: 'n',
  independent: 'n',
  guerrilla: 'n',
  civilian: 'c',
};

function forceSymbol(role: Force['role'], side?: string | null): string {
  const letter = SIDE_LETTER[(side ?? '').toLowerCase()] ?? 'n';
  return `${role === 'civilian' ? 'c' : letter}_${FORCE_ROLE[role] ?? 'unknown'}`;
}
